import React from 'react';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';
import { useMedicalProfileDirectives } from '../../../../../../hooks/api/useMedicalProfileDirectives';
import { useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../../utils/intl';
import { useHasPermission } from '../../../../../v2BaseComponents/Permissions/hooks';
import Button from '../../../../../v2BaseComponents/Button';
import { View } from 'react-native';
import { useMaybeRenderedSideDrawerToggle } from '../../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import EditPOASideDrawerContent from './EditPOASideDrawerContent';
import Stack, { contentStackSpacing } from '../../../../../v2BaseComponents/Stack';
import Text from '../../../../../v2BaseComponents/Text';
import { APITypes } from '../../../../../../primary-record-types';
import { poaRelationshipTypeMessages } from '../../../../../../utils/sharedMessages';
import FileCard from '../../../../../shared/FileCard';

const intl = getIntl('POASection', {
    selectPOA: 'Add Legal Decision-Making Details',
    editPOA: 'Edit Legal Decision-Making Details',
    poaHeader: 'Healthcare and legal decision-making details',
    files: 'Files:',
    edit: 'Edit',
});

export type POASectionOwnProps = { profileId: string };

export type POASectionProps = POASectionOwnProps;

const POASection: React.FC<POASectionProps> = (props) => {
    const editPOASideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'edit-poa',
        title: intl('editPOA'),
        renderContent: (onClose) => <EditPOASideDrawerContent medicalProfileId={props.profileId} onClose={onClose} />,
        mobileOnFocus: () => navigate('edit-legal-decision-maker', { profileId: props.profileId }),
    });
    const canEditPOA = useHasPermission(props.profileId, 'idsAndInfo', 'edit');
    const { isLoadingPOA, poa } = useMedicalProfileDirectives({
        medicalProfileId: props.profileId,
    });

    const { navigate } = useProfilesTabNavigation();

    const renderNoPOA = () => {
        if (!canEditPOA) return null;

        return (
            <Button size="lg" title={intl('selectPOA')} onPress={editPOASideDrawer.onOpen} testID="add-poa-button" />
        );
    };

    const renderPOAContent = (poa: APITypes.medicalProfileService.ProfilePOAResponse) => {
        return (
            <Stack vertical space={contentStackSpacing} align="flex-start" testID="poaContent">
                <Text variant="bold-h5">{intl('poaHeader')}</Text>
                <Stack vertical>
                    <Text variant="body-1">{`${poa.poaHealthcareProxyName}${
                        poa.poaRelationship ? ` (${poaRelationshipTypeMessages(poa.poaRelationship)})` : ''
                    }`}</Text>
                    {poa.poaPhoneNumber ? <Text variant="body-1">{poa.poaPhoneNumber}</Text> : null}
                </Stack>
                {poa?.medicalProfileFiles?.length > 0 ? (
                    <Stack vertical space={1} align="flex-start" testID="poaFiles">
                        <Text variant="bold-body-1">{intl('files')}</Text>
                        {poa.medicalProfileFiles.map((file) => (
                            <FileCard
                                key={file.medicalProfileFileId}
                                file={file}
                                onPress={() => {
                                    navigate('file', { fileId: file.medicalProfileFileId, profileId: props.profileId });
                                }}
                            />
                        ))}
                    </Stack>
                ) : null}
                {canEditPOA ? (
                    <Button
                        size="sm"
                        title={intl('edit')}
                        onPress={editPOASideDrawer.onOpen}
                        icon="Edit2"
                        testID="edit-poa-button"
                    />
                ) : null}
            </Stack>
        );
    };

    return (
        <>
            <View style={{ alignSelf: 'stretch' }}>
                <AnimateLoadedContent isLoading={isLoadingPOA} style={{ height: 'auto' }}>
                    {() => (poa ? renderPOAContent(poa) : renderNoPOA())}
                </AnimateLoadedContent>
            </View>
            {editPOASideDrawer.SideDrawer}
        </>
    );
};

export default React.memo(POASection);
